/**
 * Source file decoding and container inspection.
 *
 * decodeAudioData gives us samples but throws away the facts that matter to a
 * hardware simulator: the real bit depth, the real codec, whether the file is
 * lossless. So the container header is parsed directly for the formats where it
 * is cheap and unambiguous (WAV, FLAC, AIFF), and the rest fall back to what
 * the browser can tell us, clearly marked as such.
 *
 * SECURITY (spec 99): every read here is bounds-checked against the buffer
 * length, all offsets are validated before use, chunk walking has an iteration
 * cap, and nothing from the file is ever evaluated or interpolated into markup.
 * A malformed or hostile file produces a parse failure, never a crash or an
 * out-of-range read.
 */

import type { SourceMetadata } from '../types/system';

export class DecodeError extends Error {
  constructor(message: string, readonly detail?: string) {
    super(message);
    this.name = 'DecodeError';
  }
}

const MAX_CHUNKS = 512;

function ascii(view: DataView, offset: number, length: number): string {
  if (offset < 0 || offset + length > view.byteLength) return '';
  let s = '';
  for (let i = 0; i < length; i++) {
    const c = view.getUint8(offset + i);
    s += c >= 32 && c < 127 ? String.fromCharCode(c) : '';
  }
  return s;
}

export interface ContainerInfo {
  container: string;
  codec: string;
  lossless: boolean;
  sampleRate: number | null;
  bitDepth: number | null;
  channels: number | null;
  totalSamples: number | null;
  durationSec: number | null;
  bitrateKbps?: number;
  tags?: Record<string, string>;
  /** True when the numbers above came from the file rather than the decoder. */
  fromHeader: boolean;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// WAV / RIFF
// ---------------------------------------------------------------------------

const WAVE_FORMAT: Record<number, { name: string; lossless: boolean }> = {
  0x0001: { name: 'PCM', lossless: true },
  0x0003: { name: 'IEEE float', lossless: true },
  0x0006: { name: 'A-law', lossless: false },
  0x0007: { name: 'mu-law', lossless: false },
  0x0011: { name: 'IMA ADPCM', lossless: false },
  0xfffe: { name: 'WAVE_FORMAT_EXTENSIBLE', lossless: true },
};

function parseWav(buf: ArrayBuffer, partial = false): ContainerInfo | null {
  const v = new DataView(buf);
  if (v.byteLength < 12) return null;
  if (ascii(v, 0, 4) !== 'RIFF' && ascii(v, 0, 4) !== 'RF64') return null;
  if (ascii(v, 8, 4) !== 'WAVE') return null;

  const warnings: string[] = [];
  let offset = 12;
  let fmtTag = 0;
  let channels = 0;
  let sampleRate = 0;
  let bitDepth = 0;
  let byteRate = 0;
  let dataBytes = 0;
  let guard = 0;
  const tags: Record<string, string> = {};

  while (offset + 8 <= v.byteLength && guard++ < MAX_CHUNKS) {
    const id = ascii(v, offset, 4);
    const size = v.getUint32(offset + 4, true);
    const body = offset + 8;
    if (size < 0 || body > v.byteLength) break;

    if (id === 'fmt ' && body + 16 <= v.byteLength) {
      fmtTag = v.getUint16(body, true);
      channels = v.getUint16(body + 2, true);
      sampleRate = v.getUint32(body + 4, true);
      byteRate = v.getUint32(body + 8, true);
      bitDepth = v.getUint16(body + 14, true);
      if (fmtTag === 0xfffe && body + 26 <= v.byteLength) {
        // Extensible: the real format is the first two bytes of the SubFormat
        // GUID, and the valid bits-per-sample can differ from the container.
        const validBits = v.getUint16(body + 18, true);
        const sub = v.getUint16(body + 24, true);
        if (validBits > 0 && validBits <= bitDepth) bitDepth = validBits;
        fmtTag = sub;
      }
    } else if (id === 'data') {
      dataBytes = size;
      if (body + size > v.byteLength && !partial) {
        dataBytes = v.byteLength - body;
        warnings.push('The data chunk claims to be longer than the file. Duration is computed from the bytes actually present.');
      }
    } else if (id === 'LIST' && ascii(v, body, 4) === 'INFO') {
      let p = body + 4;
      let g2 = 0;
      while (p + 8 <= body + size && p + 8 <= v.byteLength && g2++ < 64) {
        const tid = ascii(v, p, 4);
        const tsize = v.getUint32(p + 4, true);
        const text = ascii(v, p + 8, Math.min(tsize, 256));
        const key = { INAM: 'title', IART: 'artist', IPRD: 'album', ICRD: 'date', IGNR: 'genre' }[tid];
        if (key && text) tags[key] = text;
        p += 8 + tsize + (tsize % 2);
      }
    }
    offset = body + size + (size % 2);
  }

  if (!sampleRate || !channels) return null;
  const fmt = WAVE_FORMAT[fmtTag] ?? { name: `Unknown (0x${fmtTag.toString(16)})`, lossless: false };
  const bytesPerFrame = (bitDepth / 8) * channels || 1;
  const totalSamples = dataBytes ? Math.floor(dataBytes / bytesPerFrame) : null;

  return {
    container: 'WAV (RIFF)',
    codec: fmt.name === 'IEEE float' ? `PCM ${bitDepth}-bit float` : `${fmt.name} ${bitDepth}-bit`,
    lossless: fmt.lossless,
    sampleRate,
    bitDepth,
    channels,
    totalSamples,
    durationSec: totalSamples ? totalSamples / sampleRate : null,
    bitrateKbps: byteRate ? (byteRate * 8) / 1000 : undefined,
    tags: Object.keys(tags).length ? tags : undefined,
    fromHeader: true,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// FLAC
// ---------------------------------------------------------------------------

function parseFlac(buf: ArrayBuffer, partial = false): ContainerInfo | null {
  const v = new DataView(buf);
  if (v.byteLength < 42) return null;
  if (ascii(v, 0, 4) !== 'fLaC') return null;

  const warnings: string[] = [];
  const tags: Record<string, string> = {};
  let offset = 4;
  let sampleRate = 0;
  let channels = 0;
  let bitDepth = 0;
  let totalSamples = 0;
  let guard = 0;
  let sawStreamInfo = false;

  while (offset + 4 <= v.byteLength && guard++ < MAX_CHUNKS) {
    const header = v.getUint8(offset);
    const isLast = (header & 0x80) !== 0;
    const type = header & 0x7f;
    const size = (v.getUint8(offset + 1) << 16) | (v.getUint8(offset + 2) << 8) | v.getUint8(offset + 3);
    const body = offset + 4;
    if (body + size > v.byteLength) {
      if (!partial) warnings.push('A FLAC metadata block extends past the end of the file; it was ignored.');
      break;
    }

    if (type === 0 && size >= 34) {
      // STREAMINFO. The interesting fields are bit-packed starting at byte 10.
      const b10 = v.getUint8(body + 10);
      const b11 = v.getUint8(body + 11);
      const b12 = v.getUint8(body + 12);
      const b13 = v.getUint8(body + 13);
      sampleRate = (b10 << 12) | (b11 << 4) | (b12 >> 4);
      channels = ((b12 >> 1) & 0x07) + 1;
      bitDepth = (((b12 & 0x01) << 4) | (b13 >> 4)) + 1;
      // 36-bit total sample count: 4 low bits of b13 then 4 more bytes.
      const high = b13 & 0x0f;
      const low =
        v.getUint8(body + 14) * 0x1000000 +
        v.getUint8(body + 15) * 0x10000 +
        v.getUint8(body + 16) * 0x100 +
        v.getUint8(body + 17);
      totalSamples = high * 4294967296 + low;
      sawStreamInfo = true;
    } else if (type === 4) {
      // VORBIS_COMMENT
      try {
        let p = body;
        const vendorLen = v.getUint32(p, true);
        p += 4 + vendorLen;
        const count = v.getUint32(p, true);
        p += 4;
        const dec = new TextDecoder();
        for (let i = 0; i < Math.min(count, 64) && p + 4 <= body + size; i++) {
          const len = v.getUint32(p, true);
          p += 4;
          if (p + len > body + size) break;
          const text = dec.decode(new Uint8Array(buf, p, Math.min(len, 512)));
          p += len;
          const eq = text.indexOf('=');
          if (eq > 0) tags[text.slice(0, eq).toLowerCase()] = text.slice(eq + 1);
        }
      } catch {
        warnings.push('The FLAC comment block could not be read; tags are unavailable.');
      }
    }

    offset = body + size;
    if (isLast) break;
  }

  if (!sawStreamInfo || !sampleRate) return null;
  return {
    container: 'FLAC',
    codec: `FLAC ${bitDepth}-bit`,
    lossless: true,
    sampleRate,
    bitDepth,
    channels,
    totalSamples: totalSamples || null,
    durationSec: totalSamples ? totalSamples / sampleRate : null,
    bitrateKbps: totalSamples ? (v.byteLength * 8) / (totalSamples / sampleRate) / 1000 : undefined,
    tags: Object.keys(tags).length ? tags : undefined,
    fromHeader: true,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// AIFF / AIFC
// ---------------------------------------------------------------------------

/** IEEE 754 80-bit extended precision, used by AIFF for the sample rate. */
function readExtended80(v: DataView, offset: number): number {
  if (offset + 10 > v.byteLength) return 0;
  const expon = ((v.getUint8(offset) & 0x7f) << 8) | v.getUint8(offset + 1);
  const sign = v.getUint8(offset) & 0x80 ? -1 : 1;
  let hiMant = v.getUint32(offset + 2, false);
  let loMant = v.getUint32(offset + 6, false);
  if (expon === 0 && hiMant === 0 && loMant === 0) return 0;
  if (expon === 0x7fff) return Infinity;
  const e = expon - 16383 - 31;
  return sign * (hiMant * Math.pow(2, e) + loMant * Math.pow(2, e - 32));
}

function parseAiff(buf: ArrayBuffer): ContainerInfo | null {
  const v = new DataView(buf);
  if (v.byteLength < 12 || ascii(v, 0, 4) !== 'FORM') return null;
  const form = ascii(v, 8, 4);
  if (form !== 'AIFF' && form !== 'AIFC') return null;

  let offset = 12;
  let channels = 0;
  let frames = 0;
  let bitDepth = 0;
  let sampleRate = 0;
  let compression = 'PCM';
  let guard = 0;

  while (offset + 8 <= v.byteLength && guard++ < MAX_CHUNKS) {
    const id = ascii(v, offset, 4);
    const size = v.getUint32(offset + 4, false);
    const body = offset + 8;
    if (id === 'COMM' && body + 18 <= v.byteLength) {
      channels = v.getUint16(body, false);
      frames = v.getUint32(body + 2, false);
      bitDepth = v.getUint16(body + 6, false);
      sampleRate = Math.round(readExtended80(v, body + 8));
      if (form === 'AIFC' && body + 22 <= v.byteLength) compression = ascii(v, body + 18, 4).trim() || 'PCM';
    }
    offset = body + size + (size % 2);
  }
  if (!sampleRate || !channels) return null;
  const lossless = compression === 'PCM' || compression === 'NONE' || compression === 'sowt' || compression === 'fl32';
  return {
    container: form === 'AIFC' ? 'AIFF-C' : 'AIFF',
    codec: `${compression === 'NONE' ? 'PCM' : compression} ${bitDepth}-bit`,
    lossless,
    sampleRate,
    bitDepth,
    channels,
    totalSamples: frames || null,
    durationSec: frames ? frames / sampleRate : null,
    fromHeader: true,
    warnings: [],
  };
}

// ---------------------------------------------------------------------------
// Lossy container sniffing (no full parse -- the decoder supplies the samples)
// ---------------------------------------------------------------------------

function sniffLossy(buf: ArrayBuffer, fileName: string): ContainerInfo | null {
  const v = new DataView(buf);
  if (v.byteLength < 12) return null;
  const tag4 = ascii(v, 0, 4);
  const b0 = v.getUint8(0);
  const b1 = v.getUint8(1);

  let container: string | null = null;
  let codec = 'Unknown';
  if (tag4 === 'OggS') {
    container = 'Ogg';
    codec = ascii(v, 28, 6).includes('vorbis') ? 'Vorbis' : ascii(v, 28, 8).includes('Opus') ? 'Opus' : 'Ogg (unknown codec)';
  } else if (tag4 === 'ID3' || (b0 === 0xff && (b1 & 0xe0) === 0xe0)) {
    container = 'MPEG';
    codec = 'MP3';
  } else if (ascii(v, 4, 4) === 'ftyp') {
    container = 'MP4/M4A';
    const brand = ascii(v, 8, 4);
    codec = brand === 'M4A ' || brand.startsWith('mp4') || brand.startsWith('isom') ? 'AAC' : `MP4 (${brand})`;
    if (fileName.toLowerCase().endsWith('.m4a') && brand === 'M4A ') codec = 'AAC (or ALAC)';
  } else if (tag4 === 'wvpk') {
    container = 'WavPack';
    codec = 'WavPack';
  } else if (tag4 === 'MAC ') {
    container = "Monkey's Audio";
    codec = 'APE';
  }

  if (!container) return null;
  return {
    container,
    codec,
    lossless: false,
    sampleRate: null,
    bitDepth: null,
    channels: null,
    totalSamples: null,
    durationSec: null,
    fromHeader: false,
    warnings: [
      `${codec} is a compressed format. Bit depth is not a meaningful property of the file, so the engine reports the decoder's output precision instead.`,
    ],
  };
}

/**
 * @param partial True when `buf` is only the head of the file. Structural
 * "claims to extend past the end" warnings are then meaningless -- of course it
 * does, we only read the first 64 kB -- and emitting them would train the user
 * to ignore the warnings that do matter.
 */
export function inspectContainer(buf: ArrayBuffer, fileName: string, partial = false): ContainerInfo {
  try {
    const parsed = parseFlac(buf, partial) ?? parseWav(buf, partial) ?? parseAiff(buf) ?? sniffLossy(buf, fileName);
    if (parsed) return parsed;
  } catch (err) {
    return {
      container: 'Unrecognised',
      codec: 'Unknown',
      lossless: false,
      sampleRate: null,
      bitDepth: null,
      channels: null,
      totalSamples: null,
      durationSec: null,
      fromHeader: false,
      warnings: [`The container header could not be parsed (${(err as Error).message}). Falling back to the browser decoder.`],
    };
  }
  return {
    container: 'Unrecognised',
    codec: 'Unknown',
    lossless: false,
    sampleRate: null,
    bitDepth: null,
    channels: null,
    totalSamples: null,
    durationSec: null,
    fromHeader: false,
    warnings: ['No known container signature was found. The browser decoder will be asked to try anyway.'],
  };
}

export interface DecodedSource {
  buffer: AudioBuffer;
  metadata: SourceMetadata;
  container: ContainerInfo;
}

/**
 * Decode a file into an AudioBuffer.
 *
 * `decodeAudioData` resamples to the context rate, so the context must already
 * be running at the file's rate if native-rate processing is wanted. The engine
 * handles that by inspecting the header first, creating the context at the
 * file's rate, and only then decoding.
 */
export async function decodeFile(
  file: File,
  ctx: BaseAudioContext,
): Promise<DecodedSource> {
  const raw = await file.arrayBuffer();
  const container = inspectContainer(raw, file.name);

  let buffer: AudioBuffer;
  try {
    // decodeAudioData detaches the buffer in some engines, so hand it a copy
    // and keep `raw` intact for the header we already parsed.
    buffer = await ctx.decodeAudioData(raw.slice(0));
  } catch (err) {
    throw new DecodeError(
      `This browser could not decode ${file.name}.`,
      container.codec !== 'Unknown'
        ? `The container was recognised as ${container.container} / ${container.codec}, but decodeAudioData rejected it. Browsers differ in codec support; WAV and FLAC are the most portable.`
        : (err as Error).message,
    );
  }

  const metadata: SourceMetadata = {
    fileName: file.name,
    fileSize: file.size,
    durationSec: buffer.duration,
    sampleRate: container.sampleRate ?? buffer.sampleRate,
    bitDepth: container.bitDepth,
    channels: container.channels ?? buffer.numberOfChannels,
    codec: container.codec,
    container: container.container,
    lossless: container.lossless,
    bitrateKbps: container.bitrateKbps ?? (file.size * 8) / buffer.duration / 1000,
    tags: container.tags,
  };

  return { buffer, metadata, container };
}

/** Probe a file's sample rate without decoding it. Used to size the context. */
export async function probeSampleRate(file: File): Promise<{ rate: number | null; info: ContainerInfo }> {
  // 64 kB is far more than any of these headers need.
  const head = await file.slice(0, Math.min(file.size, 65536)).arrayBuffer();
  const info = inspectContainer(head, file.name, file.size > head.byteLength);
  return { rate: info.sampleRate, info };
}
